import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { Apollo, gql } from 'apollo-angular';
import { GraphQLFormattedError } from 'graphql';
import { from, Observable, of } from 'rxjs';
import { map, mergeMap, switchMap, take, tap, timeout, toArray } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { mapGraphQLStudentToIStudent, StudentAccessIndex } from '../../graphql/functions/mappingFunctions';
import { Allocation, Data, IEmail } from '../../graphql/models/gqlModels';
import { IAccessChangeStudent } from '../../models/access';
import { IEmergencyContact, ILearnerAccess, IMedicalInfo, IStudent } from '../../models/user';
import { selectUserAccessContext } from '../../store/access/access.selectors';
import { ImageCacheActions } from '../../store/image-cache/image-cache.actions';
import { AuthService } from '../auth/auth.service';
import { LookupService } from '../lookup/lookup.service';

@Injectable({
  providedIn: 'root',
})
export class StudentService {
  private readonly documentEndpoint = '/ds-msal/upload';
  private readonly baseUrl = environment.apiBaseUrl;
  private readonly lookupService = inject(LookupService);
  private readonly authService = inject(AuthService);
  tokenSignal = toSignal(this.authService.getToken(), { initialValue: null });

  constructor(
    private readonly http: HttpClient,
    private readonly apollo: Apollo,
    private readonly store: Store,
  ) {}

  private buildStudentAccessIndex(contexts: any[] | null): StudentAccessIndex {
    const index: StudentAccessIndex = {};
    if (!contexts) return index;

    for (const ctx of contexts) {
      const accountCode = ctx?.accountCode ?? '';
      const companyCode = ctx?.companyCode ?? '';
      const ids: string[] = ctx?.studentIds ?? [];
      for (const id of ids) {
        // First-write wins; adjust if you need disambiguation across multiple contexts
        if (!index[id]) {
          index[id] = { accountCode, companyCode };
        }
      }
    }
    return index;
  }

  getStudents(students: ILearnerAccess[]): Observable<{ students: IStudent[]; errors: GraphQLFormattedError[] }> {
    if (!students?.length) {
      return of({ students: [], errors: [] });
    }

    const colorByStudentId = new Map(students.map((learnerAccess) => [learnerAccess.learnerId, learnerAccess.color]));

    // Get a snapshot of access context and construct an index
    return this.store.select(selectUserAccessContext).pipe(
      take(1),
      map((contexts) => this.buildStudentAccessIndex(contexts)),
      switchMap((accessIndex) =>
        this.apollo
          .query<Data>({
            query: gql`
              query GetStudents($ids: [ID!]!) {
                students(ids: $ids) {
                  email {
                    adUsername
                  }
                  gender
                  id
                  imageId
                  imageUrl
                  person {
                    firstName
                    lastName
                    nickName
                    idNumber
                    emailAddresses {
                      address
                    }
                    title {
                      description
                      shortCode
                    }
                  }
                  medicalAidInfo {
                    principleMemberId
                    principleMemberIdType
                    principleMemberName
                    scheme {
                      id
                    }
                    number
                  }
                  emergencyContact {
                    person {
                      lastName
                      firstName
                      emailAddresses {
                        id
                        address
                        emailAddressType {
                          id
                          description
                        }
                      }
                      phoneNumbers {
                        id
                        number
                        phoneNumberType {
                          id
                        }
                        nationalNumber
                        mobileNumber
                        countryCode
                      }
                    }
                    relationship {
                      id
                      description
                    }
                  }
                  allocations {
                    currentStatusCode
                    year
                    studentNumber
                    studentYear {
                      schoolGrade {
                        grade {
                          code
                          name
                        }
                      }
                      registerClass {
                        id
                        name
                        year
                        school {
                          id
                        }
                      }
                    }
                    account {
                      company {
                        myHubCompanySettings {
                          companyCode
                          companyName
                          itsiEnabled
                          gritEnabled
                          eeziPayEnabled
                          id
                        }
                        id
                      }
                      accountCode
                    }
                  }
                }
              }
            `,
            variables: { ids: students.map((learnerAccess) => learnerAccess.learnerId) },
            fetchPolicy: 'network-only',
            errorPolicy: 'all',
          })
          .pipe(
            timeout(50000),
            map((response) => {
              const studentsData = response.data.students ?? [];
              const queryErrors = response.errors ? [...response.errors] : [];
              const currentYear = new Date().getFullYear();

              const studentsResult = studentsData
                .map((studentData) => {
                  // Prefer current year allocation; fallback to latest available year.
                  const currentStudentYear =
                    studentData.allocations.find((a) => a.year === currentYear) ??
                    studentData.allocations.reduce<Allocation | undefined>(
                      (latest, a) => (!latest || a.year > latest.year ? a : latest),
                      undefined,
                    );

                  if (currentStudentYear?.currentStatusCode === 'CAN') {
                    console.warn(`${studentData.id} is cancelled, skipping.`);
                    return null;
                  }

                  // Create a new object with the color from ILearnerAccess since GraphQL object is not extensible
                  const studentDataWithColor = {
                    ...studentData,
                    color: colorByStudentId.get(studentData.id) ?? '',
                  };
                  return mapGraphQLStudentToIStudent(
                    studentDataWithColor,
                    this.lookupService,
                    currentStudentYear,
                    accessIndex,
                    this.tokenSignal(),
                  );
                })
                .filter((student): student is IStudent => student !== null)
                .sort((a, b) => (a.id || '').localeCompare(b.id || ''));

              return { students: studentsResult, errors: queryErrors };
            }),
            tap({
              error: (error) => console.error('Student data error:', error),
            }),
          ),
      ),
    );
  }

  getStudentsForAccessChange(
    studentIds: string[],
  ): Observable<{ students: IAccessChangeStudent[]; errors: GraphQLFormattedError[] }> {
    if (!studentIds?.length) {
      return of({ students: [], errors: [] });
    }

    return from(studentIds).pipe(
      mergeMap((studentId) => {
        return this.apollo
          .query<Data>({
            query: gql`
            query GetStudentForAccessChange {
              student(id: "${studentId}") {
                id
                color
                imageId
                imageUrl
                email {
                  adUsername
                }
                person {
                  firstName
                  lastName
                  nickName
                }
                allocations {
                  currentStatusCode
                  year
                  account {
                    accountCode
                  }
                }
              }
            }
          `,
            fetchPolicy: 'network-only',
            errorPolicy: 'all',
          })
          .pipe(
            timeout(50000),
            map((response) => {
              const studentData = response.data.student;
              const queryErrors = response.errors ? [...response.errors] : [];

              if (!studentData) {
                return { student: null as IAccessChangeStudent | null, errors: queryErrors };
              }

              const currentYear = new Date().getFullYear();
              const currentStudentYear =
                studentData.allocations.find((a) => a.year === currentYear) ??
                studentData.allocations.reduce<Allocation | undefined>(
                  (latest, a) => (!latest || a.year > latest.year ? a : latest),
                  undefined,
                );

              if (currentStudentYear?.currentStatusCode === 'CAN') {
                return { student: null as IAccessChangeStudent | null, errors: queryErrors };
              }

              let imageUrl = studentData.imageUrl
                ? studentData.imageUrl.includes('/ds-msal/webp')
                  ? `${environment.apiBaseUrl}/ds-msal/webp` + studentData.imageUrl.split('/ds-msal/webp')[1]
                  : `${environment.apiBaseUrl}${studentData.imageUrl}`
                : '';

              if (!imageUrl || imageUrl.endsWith('/null')) {
                imageUrl = '';
              }

              const student: IAccessChangeStudent = {
                id: studentData.id,
                guardianRole: null,
                color: studentData.color ?? '',
                accountCode: currentStudentYear?.account?.accountCode ?? '',
                imageUrl,
                imageId: studentData.imageId ?? null,
                person: {
                  firstName: studentData.person?.firstName ?? '',
                  lastName: studentData.person?.lastName ?? '',
                  nickName: studentData.person?.nickName ?? '',
                },
              };

              return { student, errors: queryErrors };
            }),
          );
      }),
      toArray(),
      map((results) => {
        const studentsResult = results
          .map((result) => result.student)
          .filter((student): student is IAccessChangeStudent => student !== null)
          .sort((a, b) => (a.id || '').localeCompare(b.id || ''));

        const errorsResult = results.reduce<GraphQLFormattedError[]>((acc, result) => {
          if (result.errors?.length) {
            acc.push(...result.errors);
          }
          return acc;
        }, []);

        return { students: studentsResult, errors: errorsResult };
      }),
      tap({
        error: (error) => console.error('Access-change student data error:', error),
      }),
    );
  }

  //gets document UUID from name
  checkProfileImageExists(studentId: string): Observable<any> {
    return this.http.get(`${this.baseUrl}${this.documentEndpoint}/document/${studentId}`).pipe(
      timeout(15000),
      tap({
        error: (error) => console.error('Profile image check error:', error),
      }),
    );
  }

  updateProfileImage(file: File, fileName: string, studentId: string, imageId: string): Observable<any> {
    const formData = new FormData();

    const endpoint = this.baseUrl + this.documentEndpoint;

    // Add the file to the form data
    formData.append('file', file);

    // Determine file extension based on file type, default to webp
    const fileExtension =
      file.type === 'image/webp' ? '.webp' : file.type === 'image/jpeg' || file.type === 'image/jpg' ? '.jpg' : '.webp'; // Default to webp for other types

    // Create the document metadata
    const documentMetadata = {
      documentName: fileName + fileExtension,
      documentType: {
        id: 'fec9683f943b440f8cdadbebedc34f98', // Static ID for profile pictures (PPIC)
      },
      externalGuid: studentId,
      id: imageId,
    };
    // Add the document metadata as a string to the form data
    formData.append('document', JSON.stringify(documentMetadata));

    // Post the form data to the upload endpoint
    return this.http.post(endpoint, formData).pipe(
      timeout(25000),
      tap((updatedUser) => {
        // If profile image was updated, clear the cache
        if (updatedUser) {
          this.store.dispatch(
            ImageCacheActions.profileImageUpdated({
              userId: studentId,
              newImageUrl: endpoint + '/' + documentMetadata.documentName,
            }),
          );
        }
      }),
      tap({
        error: (error) => console.error('Profile image upload error:', error),
      }),
    );
  }

  deleteProfileImage(imageId: string): Observable<any> {
    //This is done via a mutation call via the GraphQL API
    return this.apollo
      .mutate({
        mutation: gql`
        mutation {
          deleteDocument(imageId: "${imageId}") {
            deleted
          }
        }
      `,
      })
      .pipe(
        timeout(15000),
        tap({
          error: (error) => console.error('Profile image delete error:', error),
        }),
      );
  }

  updatePreferredName(studentId: string, preferredName: string): Observable<any> {
    return this.apollo
      .mutate({
        mutation: gql`
      mutation MyMutation {
        updatePerson(
          input: {personaId: "${studentId}", nickName: "${preferredName}"}
        ) {
          nickName
        }
      }
      `,
      })
      .pipe(
        timeout(15000),
        tap({
          error: (error) => console.error('Preferred name update error:', error),
        }),
      );
  }

  updateEmergencyContact(studentId: string, emergencyContact: IEmergencyContact): Observable<any> {
    return this.apollo
      .mutate({
        mutation: gql`
          mutation UpdateEmergencyContact(
            $studentId: ID!
            $firstName: String!
            $lastName: String!
            $relationship: ID!
            $phonenumber: String!
            $email: String!
            $emailType: ID!
            $phoneType: ID!
            $emailId: ID!
            $phoneId: ID!
            $countryCode: String!
            $nationalNumber: String!
          ) {
            updateEmergencyContact(
              input: {
                studentId: $studentId
                person: {
                  firstName: $firstName
                  lastName: $lastName
                  emailAddress: { id: $emailId, address: $email, emailAddressType: { id: $emailType } }
                  phoneNumber: {
                    id: $phoneId
                    number: $phonenumber
                    countryCode: $countryCode
                    phoneNumberType: { id: $phoneType }
                    nationalNumber: $nationalNumber
                    mobileNumber: true
                  }
                }
                relationship: { id: $relationship }
              }
            ) {
              person {
                firstName
                lastName
                emailAddresses {
                  id
                  address
                  emailAddressType {
                    id
                  }
                }
                phoneNumbers {
                  id
                  number
                  phoneNumberType {
                    id
                  }
                  nationalNumber
                  mobileNumber
                  countryCode
                }
              }
              relationship {
                id
              }
            }
          }
        `,
        variables: {
          studentId,
          firstName: emergencyContact.name,
          lastName: emergencyContact.surname,
          relationship: emergencyContact.relationship,
          phonenumber: this.convertNumber(emergencyContact.phoneNumber as string, emergencyContact.countryCode),
          email: emergencyContact.email,
          emailType: emergencyContact.emailType,
          phoneType: emergencyContact.phoneType,
          emailId: emergencyContact.emailId,
          phoneId: emergencyContact.phoneId,
          nationalNumber: emergencyContact.nationalNumber,
          countryCode: emergencyContact.countryCode,
        },
      })
      .pipe(
        timeout(15000),
        tap({
          error: (error) => console.error('Emergency contact update error:', error),
        }),
      );
  }

  addmergencyContactEmailAddress(studentId: string, address: IEmail): Observable<any> {
    return this.apollo
      .mutate({
        mutation: gql`
          mutation AddEmergencyContactEmailAddress($studentId: ID!, $address: String!, $emailTypeId: ID!) {
            addEmergencyContactEmailAddress(
              input: {
                studentId: $studentId
                emailAddress: { address: $address, emailAddressType: { id: $emailTypeId } }
              }
            ) {
              id
              person {
                id
                emailAddresses {
                  address
                  emailAddressType {
                    id
                    description
                  }
                  id
                }
              }
            }
          }
        `,
        variables: {
          studentId,
          address: address.address,
          emailTypeId: address.emailAddressType?.id,
        },
      })
      .pipe(
        timeout(15000),
        tap({
          error: (error) => console.error('Error adding emergency contact email address:', error),
        }),
      );
  }

  convertNumber(number: string, countryCode: string | undefined): string {
    let phoneNumber = '';

    if (countryCode && number.startsWith('0')) {
      phoneNumber = countryCode + number.substring(1);
    } else {
      phoneNumber = countryCode + number;
    }

    return phoneNumber;
  }

  updateMedicalInformation(studentId: string, medicalInformation: IMedicalInfo): Observable<any> {
    return this.apollo
      .mutate({
        mutation: gql`
      mutation UpdateMedicalInformation {
      updateMedicalAidInfo(
        input: {medicalAidInfo: {principleMemberId: "${medicalInformation.mainMemberId}", principleMemberIdType: "${medicalInformation.idType}", principleMemberName: "${medicalInformation.mainMemberName}", number: "${medicalInformation.mainMemberNumber}"}, medicalScheme: {id: "${medicalInformation.provider}"}, studentId: "${studentId}"}
      ) {
        number
        principleMemberId
        principleMemberIdType
        principleMemberName
        scheme {
          id
          description
        }
      }
    }
      `,
      })
      .pipe(
        timeout(15000),
        tap({
          error: (error) => console.error('Medical information update error:', error),
        }),
      );
  }

  getReportMetadata(studentId: string): Observable<any> {
    return this.apollo
      .query<Data>({
        query: gql`
query Reports {
student(id: "${studentId}") {
      reports {
        reportRef
        reportTerm
        reportYear
        dateCreated
      }
  }
}
      `,
      })
      .pipe(
        timeout(15000),
        tap({
          error: (error) => console.error('Report metadata error:', error),
        }),
      );
  }

  getAbsenteeRecords(studentId: string): Observable<any> {
    const currentYear = new Date().getFullYear();
    // Use the current year for the allocation criteria and limit results
    return this.apollo
      .query<Data>({
        query: gql`
query GetAbsenteeRecords {
  studentSearch(criteria: {id: "${studentId}", allocation: {year: ${currentYear}}}, limit: 200) {
    content {
      allocations {
        studentYear {
          absenteeRecords {
            date
            id
            reason
            schoolTerm
            year
            type {
              id
              defaultItem
              description
              refDataType
              shortCode
            }
            missedSubjects {
              schoolSubject {
                subject {
                  abbreviation
                }
              }
            }
          }
        }
      }
    }
  }
}
      `,
      })
      .pipe(
        timeout(10000), // Increased timeout to 10 seconds
        tap({
          error: (error) => console.error('Absentee records error:', error),
        }),
      );
  }
}
